import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from './apiClient'; // centralized axios instance

// Fetch all learners
export const fetchLearners = createAsyncThunk('learners/fetchLearners', async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get('/learners/');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch learners');
  }
});

// Create a new learner
export const createLearner = createAsyncThunk('learners/createLearner', async (learnerData, { rejectWithValue }) => {
  try {
    const response = await apiClient.post('/learners/', learnerData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create learner');
  }
});

// Delete a learner
export const deleteLearner = createAsyncThunk('learners/deleteLearner', async (id, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/learners/${id}/`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete learner');
  }
});

// Update learner status
export const updateLearnerStatus = createAsyncThunk('learners/updateLearnerStatus', async ({ id, status }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(`/learners/${id}/status`, { status });
    return { id, status: response.data.status };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update learner status');
  }
});

// Partial update learner details
export const patchLearner = createAsyncThunk('learners/patchLearner', async (learnerData, { rejectWithValue }) => {
  try {
    const { id, ...data } = learnerData;
    const response = await apiClient.patch(`/learners/${id}/`, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update learner');
  }
});

const learnersSlice = createSlice({
  name: 'learners',
  initialState: {
    learners: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLearners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLearners.fulfilled, (state, action) => {
        state.loading = false;
        state.learners = action.payload;
      })
      .addCase(fetchLearners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createLearner.pending, (state) => {
        state.loading = true;
      })
      .addCase(createLearner.fulfilled, (state, action) => {
        state.loading = false;
        state.learners.push(action.payload);
      })
      .addCase(createLearner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteLearner.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLearner.fulfilled, (state, action) => {
        state.loading = false;
        state.learners = state.learners.filter((learner) => learner.id !== action.payload);
      })
      .addCase(deleteLearner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateLearnerStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateLearnerStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { id, status } = action.payload;
        const index = state.learners.findIndex((learner) => learner.id === id);
        if (index !== -1) {
          state.learners[index].status = status;
        }
      })
      .addCase(updateLearnerStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(patchLearner.pending, (state) => {
        state.loading = true;
      })
      .addCase(patchLearner.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.learners.findIndex((learner) => learner.id === action.payload.id);
        if (index !== -1) {
          state.learners[index] = action.payload;
        }
      })
      .addCase(patchLearner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default learnersSlice.reducer;
